// @ts-nocheck TODO: fix types for the entire file

import styled from '@emotion/styled';
import { Divider } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { Box } from '@mui/system';
import { Button, IconButton } from 'components/Buttons';
import TypographyComponent from 'components/Typography/Typography';
import pxToEm from 'theme/pxToEm';
import unitsToRem from 'theme/unitsToRem';

export const SearchContent = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  cursor: default;
  display: grid;
  width: 100%;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  column-gap: ${pxToEm(50)};
  ${({ theme }) => theme.breakpoints.down('md')} {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    flex-wrap: wrap;
    row-gap: ${pxToEm(12)};
  }
`;

export const SearchContentItem = styled.li`
  display: flex;
  gap: 15px;
  padding: ${pxToEm(6)} 0;
  width: 100%;
  color: ${(props) => props.theme.palette.grey[500]};
  cursor: pointer;
  &:hover {
    color: ${(props) => props.theme.palette.primary.main};
    text-decoration: underline;
  }
`;

export const SkeletonLine = styled(Skeleton)`
  border-radius: ${pxToEm(10)};
  transform: none;
  width: 100%;
`;

export const SkeletonArrow = styled(Skeleton)`
  border-radius: ${pxToEm(10)};
  transform: none;
  margin-right: ${pxToEm(8)};
`;

export const ArrowWrapper = styled(Box)`
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.palette.grey.A400};
  padding: ${pxToEm(8)};

  &:hover {
    cursor: pointer;
  }
`;

export const RowDivider = styled(Divider, {
  shouldForwardProp: (prop) => !['shouldHide'].includes(prop),
})<{ shouldHide?: boolean }>`
  background: ${(props) => props.theme.palette.grey['200']};
  opacity: ${(props) => (props.shouldHide ? '0 !important' : 1)};
`;

interface IconContainerProps {
  isMoodboard?: boolean;
}

export const IconContainer = styled(Box, {
  shouldForwardProp: (prop) => !['isMoodboard'].includes(prop),
})<IconContainerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${(props) => (props?.isMoodboard ? unitsToRem(5.25) : pxToEm(36))};
  height: ${(props) => (props?.isMoodboard ? unitsToRem(5.25) : pxToEm(36))};
  color: ${({ theme }) => theme.palette.grey['A400']};

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: ${(props) => (props?.isMoodboard ? unitsToRem(3.5) : pxToEm(24))};
    height: ${(props) => (props?.isMoodboard ? unitsToRem(3.5) : pxToEm(24))};
  }
`;

interface ImageContainerProps {
  isCircle?: boolean;
  isMoodboard?: boolean;
}

export const ImageContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    !['isCircle', 'isMoodboard'].includes(String(prop)),
})<ImageContainerProps>`
  width: ${(props) => (props?.isMoodboard ? unitsToRem(7) : pxToEm(48))};
  height: ${(props) => (props?.isMoodboard ? unitsToRem(7) : pxToEm(48))};
  border-radius: ${(props) =>
    props?.isMoodboard ? unitsToRem(1.25) : pxToEm(props.isCircle ? 100 : 8)};
  background-color: ${({ theme }) => theme.palette.grey[300]};
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  img {
    object-fit: cover;
  }
`;

export const SkeletonImageContainer = styled(ImageContainer)`
  background-color: transparent;
`;

export const RowContainer = styled.div<{
  withHover?: boolean;
  isMoodboard?: boolean;
  isSelected?: boolean;
}>`
  display: flex;
  flex-direction: row;
  column-gap: ${(props) => (props.isMoodboard ? unitsToRem(1.25) : pxToEm(8))};
  align-items: center;
  min-height: ${(props) => (props.isMoodboard ? unitsToRem(10) : pxToEm(48))};
  padding: ${(props) => (props.isMoodboard ? unitsToRem(1.25) : pxToEm(8))}
    ${(props) => (props.isMoodboard ? unitsToRem(2) : pxToEm(16))};
  border-radius: ${(props) =>
    props.isMoodboard ? unitsToRem(1.4) : pxToEm(10)};
  .clear-icon-button {
    opacity: 0;
  }

  ${(props) =>
    props?.isSelected
      ? `
    cursor: pointer;
    background-color: ${props.theme.palette.grey['200']};

    .clear-icon-button {
      opacity: 1;
    }
    .boardPlaceholder {
      background: ${props.theme.palette.grey[300]};
    }
    ${ImageContainer}, ${BoardImagePlaceholder} {
      background: ${props.theme.palette.grey[300]};
    }
  `
      : ''}

  ${({ withHover, theme }) =>
    withHover
      ? `&:hover {
    cursor: pointer;
    background-color: ${theme.palette.grey['200']};

    .clear-icon-button {
      opacity: 1;
    }
    .boardPlaceholder {
      background: ${theme.palette.grey[300]};
    }
    ${ImageContainer}, ${BoardImagePlaceholder} {
      background: ${theme.palette.grey[300]};
    }
  }

  &:focus {
    background-color: ${theme.palette.grey['200']};
  }`
      : ''};

  ${({ theme }) => theme.breakpoints.down('md')} {
    padding-left: ${(props) =>
      props?.isMoodboard ? unitsToRem(1.25) : pxToEm(8)};
    padding-right: ${(props) =>
      props?.isMoodboard ? unitsToRem(1.25) : pxToEm(8)};
  }
`;

export const SectionHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: ${pxToEm(34)};
  column-gap: ${pxToEm(8)};
  margin-bottom: ${pxToEm(8)};
  padding-left: ${pxToEm(4)};
`;

export const SectionTitle = styled(TypographyComponent)`
  font-weight: 600;
  color: ${(props) => props.theme.palette.grey['A400']};
`;

export const BoardImageGrid = styled(Box, {
  shouldForwardProp: (prop) => !['image', 'isMoodboard'].includes(String(prop)),
})<{ image: string; isMoodboard?: boolean }>`
  overflow: hidden;
  display: flex;
  border-radius: ${(props) =>
    props?.isMoodboard ? unitsToRem(1.25) : pxToEm(8)};
  width: ${(props) => (props?.isMoodboard ? unitsToRem(3) : pxToEm(23))};
  height: ${(props) => (props?.isMoodboard ? unitsToRem(3) : pxToEm(23))};
  background-color: ${({ theme }) => theme.palette.grey[200]};
  background-image: url(${({ image }) => image});
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  img {
    object-fit: cover;
  }
`;

export const BoardImages = styled(Box, {
  shouldForwardProp: (prop) =>
    !['imageNumber', 'isMoodboard'].includes(String(prop)),
})<{ imageNumber: number; isMoodboard?: boolean }>`
  display: flex;
  width: ${(props) => (props?.isMoodboard ? unitsToRem(7) : pxToEm(48))};
  height: ${(props) => (props?.isMoodboard ? unitsToRem(7) : pxToEm(48))};
  border-radius: ${(props) =>
    props?.isMoodboard ? unitsToRem(1.25) : pxToEm(8)};
  flex-wrap: wrap;
  ${({ imageNumber, isMoodboard }) => {
    switch (imageNumber) {
      case 1:
        return `
          ${BoardImageGrid} {
            width: 100%;
            height: 100%;
            aspect-ratio: 1/1;
            border-radius: ${isMoodboard ? unitsToRem(1.25) : pxToEm(8)};
          }
        `;
      case 2:
        return `
            ${BoardImageGrid} {
              width: 50%;
              height: 100%;
              aspect-ratio: 1/0.5;
              &:nth-of-type(1) {
                border-radius: ${
                  isMoodboard ? unitsToRem(1.25) : pxToEm(8)
                } 0 0 ${isMoodboard ? unitsToRem(1.25) : pxToEm(8)};
              }
              &:nth-of-type(2) {
                border-radius: 0 ${
                  isMoodboard ? unitsToRem(1.25) : pxToEm(8)
                } ${isMoodboard ? unitsToRem(1.25) : pxToEm(8)} 0;
              }
            }
          `;
      case 3:
        return `
              flex-wrap: wrap;
              ${BoardImageGrid} {
                &:nth-of-type(1) {
                  width: 50%;
                  aspect-ratio: 1/1;
                  border-radius: ${
                    isMoodboard ? unitsToRem(1.25) : pxToEm(8)
                  } 0 0 0;
                }
                &:nth-of-type(2) {
                  width: 50%;
                  aspect-ratio: 1/1;
                  border-radius: 0 ${
                    isMoodboard ? unitsToRem(1.25) : pxToEm(8)
                  } 0 0;
                }
                &:nth-of-type(3) {
                  width: 100%;
                  aspect-ratio: 0.5/1;
                  border-radius: 0 0 ${
                    isMoodboard ? unitsToRem(1.25) : pxToEm(8)
                  } ${isMoodboard ? unitsToRem(1.25) : pxToEm(8)};
                }
              }
            `;
      case 4:
        return `
            ${BoardImageGrid} {
              flex-wrap: wrap;
              width: 50%;
              aspect-ratio: 1/1;
              border-radius: 0;
              &:nth-of-type(1) {
                border-top-left-radius: ${
                  isMoodboard ? unitsToRem(1.25) : pxToEm(8)
                };
              }
              &:nth-of-type(2) {
                border-top-right-radius: ${
                  isMoodboard ? unitsToRem(1.25) : pxToEm(8)
                };
              }
              &:nth-of-type(3) {
                border-bottom-left-radius: ${
                  isMoodboard ? unitsToRem(1.25) : pxToEm(8)
                };
              }
              &:nth-of-type(4) {
                border-bottom-right-radius: ${
                  isMoodboard ? unitsToRem(1.25) : pxToEm(8)
                };
              }
            }
          `;
      default:
        return null;
    }
  }};
`;

export const BoardImagePlaceholder = styled.div<{ isMoodboard?: boolean }>`
  border-radius: ${(props) =>
    props?.isMoodboard ? unitsToRem(1.25) : pxToEm(8)};
  width: ${(props) => (props?.isMoodboard ? unitsToRem(7) : pxToEm(48))};
  height: ${(props) => (props?.isMoodboard ? unitsToRem(7) : pxToEm(48))};
  background-color: ${({ theme }) => theme.palette.grey[300]};
`;

export const CorrectedQueryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.5em 0.75em 1em;
`;

export const CorrectionLink = styled(Button)`
  padding: 0;
  line-height: 1;
  margin-left: 3px;
  min-width: 0;
  font-size: inherit;
  color: ${({ theme }) => theme.palette.grey['A400']} !important;
  white-space: normal;
  text-align: left;
  &:hover {
    background: none;
  }
`;

export const CorrectionLine = styled(TypographyComponent)`
  display: flex;
  align-items: center;
  .resultsLabel {
    flex-shrink: 0;
  }
  & > * {
    line-height: 1;
  }
`;

export const RowClearButton = styled(IconButton)`
  opacity: 1;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    display: none;
  }
`;

export const HistoryClearButton = styled(Button)`
  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.palette.primary.main};
    background-color: ${({ theme }) => theme.palette.grey['200']} !important;
  }

  &:focus {
    color: ${({ theme }) => theme.palette.primary.main};
  }

  &:active {
    background-color: ${({ theme }) => theme.palette.grey['50']} !important;
  }
`;
